/** PrioridadesTab — CRUD de 4 prioridades con SLA en horas. */
import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';

const empty = { nombre: '', color: '#3B82F6', nivel: 1, horas_sla: 24, activo: true, orden: 0 };

const formatSla = (h) => {
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rest = h % 24;
    return rest ? `${d}d ${rest}h` : `${d}d`;
  }
  return `${h}h`;
};

export default function PrioridadesTab({ api }) {
  const { user } = useAuth();
  const isAdmin = ['ADMINISTRADOR', 'SUPERADMIN'].includes(user?.rol);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets/prioridades');
      setItems(data);
    } catch (e) { toast.error('Error cargando prioridades'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setDialogOpen(true); };
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar la prioridad "${it.nombre}"?`)) return;
    try { await api.delete(`/tickets/prioridades/${it.id}`); toast.success('Prioridad eliminada'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error eliminando'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        nivel: parseInt(form.nivel, 10),
        horas_sla: parseInt(form.horas_sla, 10),
        orden: parseInt(form.orden || 0, 10),
      };
      if (editing) await api.put(`/tickets/prioridades/${editing.id}`, payload);
      else await api.post('/tickets/prioridades', payload);
      toast.success(editing ? 'Prioridad actualizada' : 'Prioridad creada');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">4 niveles (1=Crítica, 4=Baja). El SLA es el tiempo máximo de resolución.</p>
        {isAdmin && (
          <Button onClick={openCreate} size="sm" data-testid="btn-nueva-prioridad">
            <Plus className="w-4 h-4 mr-1" /> Nueva prioridad
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="border rounded-md divide-y">
          {items.length === 0 && <div className="px-4 py-6 text-sm text-slate-500 text-center">Sin prioridades</div>}
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-3 px-4 py-3" data-testid={`prioridad-row-n${it.nivel}`}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: it.color || '#cbd5e1' }} />
              <div className="flex-1">
                <div className="text-sm font-medium flex items-center gap-2">
                  <span className="text-xs font-mono text-slate-400">N{it.nivel}</span>
                  {it.nombre}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" /> SLA {formatSla(it.horas_sla)}
                </div>
              </div>
              <Badge variant={it.activo ? 'default' : 'secondary'}>{it.activo ? 'Activo' : 'Inactivo'}</Badge>
              {isAdmin && (
                <>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-prioridad-n${it.nivel}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(it)} data-testid={`del-prioridad-n${it.nivel}`}>
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Editar prioridad' : 'Nueva prioridad'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nivel (1 a 4)</Label>
                <Input type="number" min="1" max="4" value={form.nivel} onChange={(e) => setForm({ ...form, nivel: e.target.value })} />
              </div>
              <div>
                <Label>Color</Label>
                <Input type="color" value={form.color || '#3B82F6'} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 p-1" />
              </div>
            </div>
            <div>
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Crítica / Alta / Media / Baja" />
            </div>
            <div>
              <Label>SLA (horas)</Label>
              <Input type="number" min="1" value={form.horas_sla} onChange={(e) => setForm({ ...form, horas_sla: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">Sugerencias: Crítica 4h · Alta 24h · Media 72h · Baja 168h.</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
              <Label>Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-guardar-prioridad">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
